"use client"

import { useEffect, useMemo, useRef } from "react"
import { KeyboardShortcut, useKeyboardShortcutContext } from "@/contexts/keyboard-shortcut-context"
import { formatShortcutKeys } from "@/utils/format-shortcut-keys"
import { useHotkeys } from "react-hotkeys-hook"

const EDITABLE_ROLES = new Set(["option"])

function shouldIgnoreShortcut(event: KeyboardEvent) {
  const targetElement = event.target instanceof HTMLElement ? event.target : null

  const role = targetElement?.getAttribute("role")?.toLowerCase()
  if (role && EDITABLE_ROLES.has(role)) {
    return true
  }

  return false
}

interface UseKeyboardShortcutOptions {
  description: string
  category?: KeyboardShortcut["category"]
  displayKeys?: string | string[] // Custom display for the shortcut
  scope?: string // Scope for hotkey (default: "*" which is global)
  useKey?: boolean // For special characters like "?"
  enableOnFormTags?: boolean
}

export function useKeyboardShortcut(keys: string, handler: () => void, options: UseKeyboardShortcutOptions) {
  const { registerShortcut } = useKeyboardShortcutContext()
  const { description, category = "Page", displayKeys, scope = "*", useKey, enableOnFormTags } = options

  // Store handler in a ref to avoid re-registering on every render
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  // Detect if this is a sequential hotkey
  const isSequential = keys.includes(">")

  // Stabilize displayKeys for dependency array
  const displayKeysJson = displayKeys ? JSON.stringify(displayKeys) : undefined
  const os = typeof navigator !== "undefined" ? navigator.platform.toLowerCase() : ""

  const parsedDisplayKeys = useMemo(() => {
    if (!displayKeysJson) return undefined
    return JSON.parse(displayKeysJson) as string | string[]
  }, [displayKeysJson])

  const formattedKeys = useMemo(() => {
    if (parsedDisplayKeys) {
      return Array.isArray(parsedDisplayKeys) ? parsedDisplayKeys : [parsedDisplayKeys]
    }

    const formatted = formatShortcutKeys(keys, os)
    if (Array.isArray(formatted)) return formatted

    return formatted ? [formatted] : undefined
  }, [parsedDisplayKeys, keys, os])

  useHotkeys(
    keys,
    (event) => {
      if (shouldIgnoreShortcut(event)) {
        return
      }

      event.preventDefault()
      handlerRef.current()
    },
    {
      scopes: scope,
      preventDefault: true,
      // Only add sequenceTimeoutMs if it's a sequential hotkey
      ...(isSequential && { sequenceTimeoutMs: 1000 }),
      // Use the produced key for special characters
      ...(useKey && { useKey: true }),
      ...(enableOnFormTags && { enableOnFormTags: true }),
    }
  )

  useEffect(() => {
    const shortcut: KeyboardShortcut = {
      id: `${keys}-${description}`,
      keys,
      description,
      category,
      formatted: formattedKeys,
      scope,
    }

    const unregister = registerShortcut(shortcut)
    return unregister
  }, [keys, description, category, registerShortcut, formattedKeys, scope])
}
